import { useCallback, useEffect } from "react";
import { Form } from "react-bootstrap";
import { XLg } from "react-bootstrap-icons";
import type {
  CaseMetadata,
  CollectionItem,
  DocumentSubType,
  ServiceManner,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import type {
  CaseResponse,
  FirmClient,
  JurisdictionResponse,
} from "@/lib/types";
import PleadingField from "./Components/PleadingField";
import styles from "./ReviewPage.module.scss";
import {
  formatGenericInfoName,
  genericInfoHandleChange,
  genericInfoHandleRemove,
  pleadCollectionAdd,
  pleadCollectionChange,
  pleadCollectionRemove,
} from "./utils";

function ReviewPageThreePd({
  _case,
  caseType: _caseType,
  damages,
  incidentDate,
  incidentDescription,
  incidentLocation,
  incidentTime,
  jurisdictions: _jurisdictions,
  firm: _firm,
  genericInfo,
  injuries,
  agreements,
  timelineDetails,
  causesOfAction,
  setCaseType,
  setIncidentTime,
  setIncidentLocation,
  setCausesOfAction,
  setDamages,
  setGenericInfo,
  setInjuries,
  setAgreements,
  setIncidentDate,
  setIncidentDescription,
  setTimelineDetails,
}: {
  _case: CaseResponse | undefined;
  caseType: string | null | undefined;
  damages: string | null | undefined;
  genericInfo: { [key: string]: string } | null | undefined;
  injuries: Array<CollectionItem> | null;
  agreements: Array<CollectionItem> | null;
  incidentDate: string | null | undefined;
  incidentDescription: string | null | undefined;
  incidentLocation: string | null | undefined;
  incidentTime: string | null | undefined;
  timelineDetails: string | null | undefined;
  caseMetaData: CaseMetadata | undefined;
  jurisdictions: JurisdictionResponse[];
  firm: FirmClient | undefined;
  jurisdiction: JurisdictionResponse | undefined;
  causesOfAction: Array<CollectionItem> | null;
  documentSubType: DocumentSubType;
  setDocumentSubType: React.Dispatch<React.SetStateAction<DocumentSubType>>;
  setDateOfService: React.Dispatch<React.SetStateAction<string | undefined>>;
  methodOfService: ServiceManner | undefined;
  setCaseMetaData: React.Dispatch<
    React.SetStateAction<CaseMetadata | undefined>
  >;
  setIncidentTime: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setIncidentLocation: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setCausesOfAction: React.Dispatch<
    React.SetStateAction<Array<CollectionItem> | null>
  >;
  setCaseType: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setDamages: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setGenericInfo: React.Dispatch<
    React.SetStateAction<{ [key: string]: string } | null | undefined>
  >;
  setInjuries: React.Dispatch<
    React.SetStateAction<Array<CollectionItem> | null>
  >;
  setAgreements: React.Dispatch<
    React.SetStateAction<Array<CollectionItem> | null>
  >;
  setIncidentDate: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setIncidentDescription: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setTimelineDetails: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
}) {
  // Replace the default extracted values from the document if existing case details are available
  const setExistingCaseDetails = useCallback(async () => {
    if (!_case) return;

    const {
      damages,
      incidentDate,
      incidentTime,
      incidentLocation,
      incidentDescription,
      genericInfo,
      caseType,
      injuries,
      agreements,
      causesOfAction,
    } = _case;

    if (damages) setDamages(damages);
    if (incidentDate) setIncidentDate(incidentDate);
    if (incidentTime) setIncidentTime(incidentTime);
    if (incidentLocation) setIncidentLocation(incidentLocation);
    if (incidentDescription) setIncidentDescription(incidentDescription);
    if (genericInfo) setGenericInfo(genericInfo);
    if (caseType) setCaseType(caseType);
    if (injuries && injuries.length > 0) setInjuries(injuries);
    if (agreements && agreements.length > 0) setAgreements(agreements);
    if (causesOfAction && causesOfAction.length > 0)
      setCausesOfAction(causesOfAction);
  }, [
    _case,
    setDamages,
    setIncidentDate,
    setIncidentTime,
    setCaseType,
    setGenericInfo,
    setInjuries,
    setIncidentDescription,
    setIncidentLocation,
    setAgreements,
    setCausesOfAction,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger on mount
  useEffect(() => {
    // Update form field states if there is existing '_case' information
    setExistingCaseDetails();
  }, []);

  return (
    <div>
      <div className={styles.pendingSection}>
        <div className={styles.pendingSectionFields}>
          <Form.Group>
            <PleadingField
              label="Damages Amount"
              setField={setDamages}
              pleadingValue={damages}
            />
          </Form.Group>
        </div>
      </div>
      {/* MAP OUT CoAs IF THEY EXIST */}
      {causesOfAction && causesOfAction.length > 0 && (
        <div className={styles.pendingSection}>
          <div className={styles.pendingSectionHeader}>
            <p>Causes of Action</p>
          </div>
          <div className={styles.pendingSectionFields}>
            {causesOfAction.map((item, index) => (
              <div
                key={`coa-${item.number}`}
                className={`${styles.allegationField} ${styles.noPadding}`}
              >
                <Form.Group
                  key={`coa-${item.number}`}
                  controlId={`coa-formText${item.number}`}
                >
                  {
                    //Eric: not sure why i need the +1 in the label here so it doesn't start with 0 here, but not for some of the other collections
                  }
                  <PleadingField
                    label={`Cause of Action ${(item.number ? item.number : index) + 1}`}
                    isTextArea
                    pleadingCollectionIndex={index}
                    mappedFieldHandler={pleadCollectionChange}
                    pleadingValue={item.text}
                    pleadingCollection={causesOfAction}
                    setCollection={setCausesOfAction}
                  />
                  <button
                    type="button"
                    className={styles.pendingRemove}
                    onClick={() =>
                      pleadCollectionRemove(
                        index,
                        causesOfAction,
                        setCausesOfAction,
                      )
                    }
                  >
                    <XLg />
                  </button>
                </Form.Group>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                pleadCollectionAdd(causesOfAction, setCausesOfAction)
              }
              className={styles.actionButton}
            >
              + Add Another
            </Button>
          </div>
        </div>
      )}
      {/* MAP OUT INJURIES IF THEY EXIST */}
      {injuries && injuries.length > 0 && (
        <div className={styles.pendingSection}>
          <div className={styles.pendingSectionHeader}>
            <p>Injuries</p>
          </div>
          <div className={styles.pendingSectionFields}>
            {injuries !== null && injuries.length > 0 && (
              <>
                {injuries.map((item, index) => (
                  <div
                    className={`${styles.allegationField} ${styles.noPadding}`}
                    key={`injury-${item.number}`}
                  >
                    <Form.Group
                      key={`injury-${item.number}`}
                      controlId={`injury-formText${item.number}`}
                    >
                      <PleadingField
                        label={`Injury ${item.number}`}
                        isTextArea
                        pleadingCollectionIndex={index}
                        mappedFieldHandler={pleadCollectionChange}
                        pleadingValue={item.text}
                        pleadingCollection={injuries}
                        setCollection={setInjuries}
                      />
                      <button
                        type="button"
                        className={styles.pendingRemove}
                        onClick={() =>
                          pleadCollectionRemove(index, injuries, setInjuries)
                        }
                      >
                        <XLg />
                      </button>
                    </Form.Group>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => pleadCollectionAdd(injuries, setInjuries)}
                  className={styles.actionButton}
                >
                  + Add Another
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {/* MAP OUT AGREEMENTS IF THEY EXIST */}
      {agreements && agreements.length > 0 && (
        <div className={styles.pendingSection}>
          <div className={styles.pendingSectionHeader}>
            <p>Agreements</p>
          </div>
          <div className={styles.pendingSectionFields}>
            {agreements !== null && agreements.length > 0 && (
              <>
                {agreements.map((item, index) => (
                  <div
                    className={`${styles.allegationField} ${styles.noPadding}`}
                    key={`agreement-${item.number}`}
                  >
                    <Form.Group
                      key={`agreement-${item.number}`}
                      controlId={`agreement-formText${item.number}`}
                    >
                      <PleadingField
                        label={`Agreement ${item.number}`}
                        isTextArea
                        pleadingCollectionIndex={index}
                        mappedFieldHandler={pleadCollectionChange}
                        pleadingValue={item.text}
                        pleadingCollection={agreements}
                        setCollection={setAgreements}
                      />
                      <button
                        type="button"
                        className={styles.pendingRemove}
                        onClick={() =>
                          pleadCollectionRemove(
                            index,
                            agreements,
                            setAgreements,
                          )
                        }
                      >
                        <XLg />
                      </button>
                    </Form.Group>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => pleadCollectionAdd(agreements, setAgreements)}
                  className={styles.actionButton}
                >
                  + Add Another
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {genericInfo && (
        <div className={styles.pendingSection}>
          <div className={styles.pendingSectionHeader}>
            <p>Other Details</p>
          </div>
          <div className={styles.pendingSectionFields}>
            {genericInfo &&
              Object.keys(genericInfo).length > 0 &&
              Object.entries(genericInfo)
                /* Sort generic info by their name */
                .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                .map(([key, value], index) => (
                  <div
                    key={`generic-${key}-${index}`}
                    className={`${styles.allegationField} ${styles.noPadding}`}
                  >
                    <Form.Group
                      key={`${key}-${index}`}
                      controlId={`formText${index}`}
                    >
                      <div className="flex-grow-1">
                        <PleadingField
                          label={formatGenericInfoName(key)}
                          isTextArea
                          genericKey={key}
                          genericInfoObj={genericInfo}
                          setGenericInfo={setGenericInfo}
                          pleadingGenericInfoHandler={genericInfoHandleChange}
                          pleadingValue={value}
                        />
                      </div>
                      <button
                        type="button"
                        className={styles.pendingRemove}
                        onClick={() =>
                          genericInfoHandleRemove(
                            key,
                            genericInfo,
                            setGenericInfo,
                          )
                        }
                      >
                        <XLg />
                      </button>
                    </Form.Group>
                  </div>
                ))}
          </div>
        </div>
      )}
      <div className={styles.pendingSection}>
        <div className={styles.pendingSectionHeader}>
          <p>Incident Details</p>
        </div>
        <div className={styles.pendingSectionFields}>
          <Form.Group>
            <PleadingField
              label="Incident Date"
              setField={setIncidentDate}
              pleadingValue={incidentDate}
            />
            <PleadingField
              isTextArea
              label="Incident Location"
              setField={setIncidentLocation}
              pleadingValue={incidentLocation}
            />
            <PleadingField
              label="Incident Time"
              setField={setIncidentTime}
              pleadingValue={incidentTime}
            />
            <PleadingField
              isTextArea
              label="Incident Description"
              setField={setIncidentDescription}
              pleadingValue={incidentDescription}
            />
          </Form.Group>
        </div>
      </div>
      <div className={styles.pendingSection}>
        <div className={styles.pendingSectionHeader}>
          <p>Timeline</p>
        </div>
        <div className={styles.pendingSectionFields}>
          <Form.Group>
            <PleadingField
              isTextArea
              label="Details"
              setField={setTimelineDetails}
              pleadingValue={timelineDetails}
            />
          </Form.Group>
        </div>
      </div>
    </div>
  );
}

export default ReviewPageThreePd;
